import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { InterventionDialog } from "@/components/care/InterventionDialog";
import { AnStatusBadge } from "@/lib/sav";
import { fmtDate } from "@/lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";

export function EquipmentSavPanel({ eq, canManage }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [siteEq, setSiteEq] = useState([]);
  const [plan, setPlan] = useState(false);
  const load = useCallback(() => { api.get(`/care/equipment/${eq.id}/sav`).then((r) => setData(r.data)).catch(() => {}); }, [eq.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (plan) api.get("/care/equipment", { params: { site_id: eq.site_id } }).then((r) => setSiteEq(r.data.items || [])).catch(() => {});
  }, [plan, eq.site_id]);
  const toggle = async (v) => {
    try { await api.post(`/care/equipment/${eq.id}/client-visibility`, { visible: v }); toast.success(v ? "Équipement visible dans l'espace client" : "Équipement masqué au client"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!data) return null;
  return (
    <Card className="p-4" data-testid="equipment-sav-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Switch checked={data.client_visible} onCheckedChange={toggle} disabled={!canManage} data-testid="eq-client-visible" />
          {data.client_visible ? "Visible dans l'espace client" : "Masqué au client"}
        </label>
        {canManage && <Button size="sm" variant="outline" onClick={() => setPlan(true)} data-testid="eq-plan-intervention"><Icons.CalendarPlus className="mr-1.5 h-4 w-4" />Planifier une intervention</Button>}
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">Signalements ({data.anomalies.length})</h3>
          {data.anomalies.length === 0 ? <p className="text-sm text-slate-400">Aucun signalement.</p>
            : <ul className="space-y-1.5">{data.anomalies.map((a) => (
                <li key={a.id}><button onClick={() => navigate(`/care/anomalies/${a.id}`)} className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 p-2 text-left text-sm hover:bg-slate-50" data-testid={`eq-anomaly-${a.id}`}>
                  <span className="min-w-0 truncate"><span className="font-mono-data text-xs text-slate-400">{a.reference}</span> {a.type_label}</span><AnStatusBadge status={a.status} /></button></li>))}</ul>}
        </div>
        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">Interventions SIGNA TRACK ({data.interventions.length})</h3>
          {data.interventions.length === 0 ? <p className="text-sm text-slate-400">Aucune intervention liée.</p>
            : <ul className="space-y-1.5">{data.interventions.map((w) => (
                <li key={w.id}><button onClick={() => navigate(`/track/${w.id}`)} className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 p-2 text-left text-sm hover:bg-slate-50" data-testid={`eq-int-${w.id}`}>
                  <span className="min-w-0 truncate"><span className="font-mono-data text-xs text-slate-400">{w.reference}</span> {w.title}</span>
                  <span className="shrink-0 text-xs text-slate-500">{w.schedule_confirmed && w.planned_start ? fmtDate(w.planned_start) : w.status_label}{w.sav_client_schedule ? " · publiée" : ""}</span></button></li>))}</ul>}
        </div>
      </div>
      <InterventionDialog open={plan} onOpenChange={setPlan} siteId={eq.site_id} equipment={siteEq} preselect={[eq.id]} onDone={load} />
    </Card>
  );
}
